import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowUp, Check, Copy, Share2, Users, Zap } from 'lucide-react';

/**
 * Public waitlist status page — the viral surface.
 * /status/:code shows queue position, referral link, and the skip-the-line
 * trial CTA. Shareable: anyone with the link sees the position live.
 */
export default function WaitlistStatusPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/?ref=${code}`;
  const shareText = encodeURIComponent(
    `AI agents find the money your store is leaking — I'm in line on Commerce Index. Join with my link and we both move up: ${shareUrl}`
  );

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`/api/growth/waitlist/status/${code}`);
      if (!r.ok) {
        setError('This queue link is invalid or expired.');
        return;
      }
      setStatus(await r.json());
    } catch (e) {
      setError('Could not load queue status.');
    }
  }, [code]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 30000); // live-ish position updates
    return () => clearInterval(t);
  }, [fetchStatus]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard unavailable */ }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white flex items-center justify-center px-4 py-16"
         style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      <div className="max-w-lg w-full text-center">
        <div className="text-xs uppercase tracking-widest text-[#64748B] mb-3">
          Commerce Index · Onboarding Queue
        </div>

        {error && (
          <div className="text-[#FF6B6B] mb-6">{error}</div>
        )}

        {status && status.status === 'waiting' && (
          <>
            <div className="text-[#94A3B8] mb-2">You're in line at</div>
            <div className="text-7xl font-bold text-[#3EF1C7] mb-2">
              #{status.position?.toLocaleString()}
            </div>
            <p className="text-sm text-[#94A3B8] mb-8 max-w-md mx-auto">
              We calibrate agents for a limited number of stores each week.
              Move up — or skip the line entirely.
            </p>

            {/* Skip the line — the revenue valve */}
            <button
              onClick={() => navigate('/checkout?plan=starter&billing=monthly')}
              className="w-full mb-3 px-6 py-4 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold hover:bg-[#3EF1C7]/90 transition-colors flex items-center justify-center gap-2"
            >
              <Zap className="w-5 h-5" /> Skip the line — start 14-day free trial
            </button>
            <div className="text-xs text-[#64748B] mb-8">
              Paid plans onboard today. Cancel anytime.
            </div>

            {/* Referral mechanics */}
            <div className="p-5 rounded-xl border border-[#22304A] bg-[#0D1628] text-left mb-4">
              <div className="flex items-center gap-2 mb-3">
                <ArrowUp className="w-4 h-4 text-[#3EF1C7]" />
                <span className="font-semibold text-sm">
                  Each friend who joins moves you up {status.boost_per_referral || 25} spots
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 px-3 py-2 rounded-lg bg-[#0B1120] border border-[#22304A] text-xs text-[#94A3B8]"
                  onFocus={e => e.target.select()}
                />
                <button
                  onClick={copyLink}
                  className="px-4 py-2 rounded-lg bg-[#1E293B] hover:bg-[#22304A] transition-colors text-sm flex items-center gap-1.5"
                >
                  {copied ? <Check className="w-4 h-4 text-[#3EF1C7]" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <a
                  href={`https://wa.me/?text=${shareText}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-1 px-3 py-2 rounded-lg bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] text-sm text-center hover:bg-[#25D366]/20 transition-colors"
                >
                  Share on WhatsApp
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${shareText}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-1 px-3 py-2 rounded-lg bg-[#1E293B] text-sm text-center hover:bg-[#22304A] transition-colors flex items-center justify-center gap-1.5"
                >
                  <Share2 className="w-4 h-4" /> Post it
                </a>
              </div>
              {status.referrals && (
                <div className="mt-3 flex items-center gap-2 text-xs text-[#64748B]">
                  <Users className="w-3.5 h-3.5" />
                  {status.referrals.referrals_total || 0} joined with your link
                  {status.referrals.credits_earned > 0 &&
                    ` · $${(status.referrals.credits_earned / 100).toFixed(0)} in agent credits earned`}
                </div>
              )}
            </div>

            <div className="text-xs text-[#64748B]">
              Connect your Shopify store after onboarding starts to jump another 100 spots.
              Both you and each friend get $5 in agent credits when they activate.
            </div>
          </>
        )}

        {status && status.status !== 'waiting' && (
          <>
            <div className="text-5xl mb-4">🎉</div>
            <h1 className="text-2xl font-bold mb-2">You're in.</h1>
            <p className="text-[#94A3B8] mb-8">
              {status.status === 'skipped_paid'
                ? 'Your trial is active — agents are calibrating on your store now.'
                : 'Your onboarding is active. Head to your dashboard.'}
            </p>
            <button
              onClick={() => navigate('/portfolio')}
              className="px-8 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold hover:bg-[#3EF1C7]/90 transition-colors"
            >
              Open my dashboard
            </button>
          </>
        )}

        {!status && !error && (
          <div className="text-[#64748B]">Loading your queue position…</div>
        )}
      </div>
    </div>
  );
}
